import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '@/components/SEO';
import { 
  Mail, 
  MessageCircle, 
  Phone, 
  MapPin, 
  Clock, 
  Send, 
  CheckCircle,
  Users,
  Building,
  Headphones,
  Globe,
  Star,
  Heart,
  Zap,
  Shield,
  ArrowRight,
  Smartphone,
  Laptop,
  Coffee,
  Calendar,
  HelpCircle,
  FileText,
  Settings,
  AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const contactMethods = [
  {
    icon: Mail,
    title: 'Email Support',
    description: 'Get detailed help with your questions',
    contact: 'support@dailzy.com',
    action: 'Send Email',
    color: 'from-red-500 to-red-600',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    responseTime: '&lt; 24 hours',
    availability: '24/7'
  },
  {
    icon: MessageCircle,
    title: 'Live Chat',
    description: 'Instant support through our app',
    contact: 'Available in mobile app',
    action: 'Start Chat',
    color: 'from-orange-500 to-orange-600',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-600',
    responseTime: '&lt; 5 minutes',
    availability: '6 AM - 11 PM'
  },
  {
    icon: Phone,
    title: 'Phone Support',
    description: 'Speak directly with our team',
    contact: '+91 98765 43210',
    action: 'Call Now',
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    responseTime: 'Immediate',
    availability: '9 AM - 8 PM'
  },
  {
    icon: Headphones,
    title: 'WhatsApp',
    description: 'Quick support on WhatsApp',
    contact: '+91 98765 43210',
    action: 'Message Us',
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-50',
    textColor: 'text-green-600',
    responseTime: '&lt; 30 minutes',
    availability: '8 AM - 10 PM'
  }
];

const departments = [
  {
    icon: Users,
    title: 'General Support',
    description: 'Account help, order issues, app problems',
    email: 'support@dailzy.com'
  },
  {
    icon: Building,
    title: 'Vendor Relations',
    description: 'Partner with us, vendor onboarding',
    email: 'vendors@dailzy.com'
  },
  {
    icon: Globe,
    title: 'Business Inquiries',
    description: 'Partnerships, investments, media',
    email: 'business@dailzy.com'
  },
  {
    icon: Shield,
    title: 'Security & Privacy',
    description: 'Report security issues, privacy concerns',
    email: 'security@dailzy.com'
  }
];

const faqs = [
  {
    question: 'How fast is the delivery?',
    answer: 'We deliver in 10-20 minutes across all our service areas.',
    category: 'Delivery'
  },
  {
    question: 'What areas do you serve?',
    answer: 'We currently serve 50+ cities across India with plans for rapid expansion.',
    category: 'Service'
  },
  {
    question: 'Is there a minimum order value?',
    answer: 'No minimum order value. Order anything from ₹10 to ₹10,000.',
    category: 'Orders'
  },
  {
    question: 'How do I become a vendor?',
    answer: 'Visit our vendor portal or contact vendors@dailzy.com to get started.',
    category: 'Vendors'
  }
];

const officeLocations = [
  {
    city: 'Mumbai',
    address: 'Bandra Kurla Complex, Mumbai 400051',
    type: 'Headquarters',
    phone: '+91 98765 43210'
  },
  {
    city: 'Bangalore',
    address: 'Koramangala, Bangalore 560034',
    type: 'Tech Hub',
    phone: '+91 98765 43211'
  },
  {
    city: 'Delhi',
    address: 'Connaught Place, New Delhi 110001',
    type: 'Operations',
    phone: '+91 98765 43212'
  }
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    category: '',
    message: '',
    priority: 'medium'
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');
  const [selectedFAQ, setSelectedFAQ] = useState<number | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setSubmitted(true);
    setIsSubmitting(false);
    setTimeout(() => setSubmitted(false), 5000);
    setFormData({ 
      name: '', 
      email: '', 
      phone: '', 
      subject: '', 
      category: '', 
      message: '', 
      priority: 'medium' 
    });
  };

  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Dailzy",
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+91-98765-43210",
          "contactType": "customer service",
          "availableLanguage": ["English", "Hindi"],
          "hoursAvailable": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "09:00",
            "closes": "20:00"
          }
        },
        {
          "@type": "ContactPoint",
          "email": "support@dailzy.com",
          "contactType": "customer support",
          "availableLanguage": ["English", "Hindi"]
        },
        {
          "@type": "ContactPoint", 
          "email": "vendors@dailzy.com",
          "contactType": "vendor relations",
          "availableLanguage": ["English", "Hindi"]
        },
        {
          "@type": "ContactPoint",
          "email": "business@dailzy.com", 
          "contactType": "business partnerships",
          "availableLanguage": ["English", "Hindi"]
        }
      ],
      "address": [
        {
          "@type": "PostalAddress",
          "addressLocality": "Mumbai",
          "addressRegion": "Maharashtra",
          "addressCountry": "IN",
          "streetAddress": "Bandra Kurla Complex"
        },
        {
          "@type": "PostalAddress",
          "addressLocality": "Bangalore", 
          "addressRegion": "Karnataka",
          "addressCountry": "IN",
          "streetAddress": "Koramangala"
        },
        {
          "@type": "PostalAddress",
          "addressLocality": "Delhi",
          "addressRegion": "Delhi",
          "addressCountry": "IN", 
          "streetAddress": "Connaught Place"
        }
      ]
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://dailzy.com/"
        },
        {
          "@type": "ListItem", 
          "position": 2,
          "name": "Contact Us",
          "item": "https://dailzy.com/contact"
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-orange-50">
      <SEO
        title="Contact Dailzy - Customer Support & Business Inquiries | Get Help"
        description="Contact Dailzy customer support team. Get help with orders, vendor partnerships, or business inquiries. Available 24/7 via phone, email, WhatsApp, and live chat."
        keywords="contact dailzy, customer support, help center, vendor support, business inquiries, phone support, live chat, customer service, contact information"
        url="https://dailzy.com/contact"
        type="website"
        structuredData={contactStructuredData}
      />
      {/* Enhanced Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-white via-red-50 to-orange-50 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-red-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute top-40 right-20 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-20 left-40 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto px-4 text-center max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <motion.div 
              className="inline-flex items-center gap-3 bg-gradient-to-r from-red-100 to-orange-100 text-red-600 rounded-2xl px-6 py-3 mb-8 shadow-lg"
              whileHover={{ scale: 1.05 }}
            >
              <Heart size={20} className="animate-pulse" />
              <span className="font-bold text-lg">We're Here to Help</span>
            </motion.div>
            
            <h1 className="text-6xl lg:text-7xl font-bold text-gray-900 mb-8 leading-tight">
              Get in{" "}
              <span className="relative">
                <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                  Touch
                </span>
                <motion.div
                  className="absolute -bottom-4 left-0 right-0 h-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </span>
            </h1>
            
            <motion.p 
              className="text-2xl text-gray-600 mb-12 leading-relaxed max-w-4xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Have questions, feedback, or need support? Our dedicated team is here to help you with anything related to your Dailzy experience.
              <span className="font-semibold text-gray-800"> We're just a message away!</span>
            </motion.p>

            {/* Quick Stats */}
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <div className="text-center p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                <div className="text-3xl font-bold text-gray-800 mb-1">&lt; 5 min</div>
                <div className="text-sm font-medium text-gray-600">Response Time</div>
              </div>
              <div className="text-center p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                <div className="text-3xl font-bold text-gray-800 mb-1">24/7</div>
                <div className="text-sm font-medium text-gray-600">Support Available</div>
              </div>
              <div className="text-center p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                <div className="text-3xl font-bold text-gray-800 mb-1">99%</div>
                <div className="text-sm font-medium text-gray-600">Satisfaction Rate</div>
              </div>
              <div className="text-center p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                <div className="text-3xl font-bold text-gray-800 mb-1">50+</div>
                <div className="text-sm font-medium text-gray-600">Cities Covered</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <div className="flex bg-gray-100 rounded-2xl p-2 gap-2">
              {[
                { id: 'contact', label: 'Contact Us', icon: MessageCircle },
                { id: 'faq', label: 'FAQ', icon: HelpCircle },
                { id: 'locations', label: 'Locations', icon: MapPin }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-white text-red-600 shadow-lg'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <AnimatePresence mode="wait">
        {activeTab === 'contact' && (
          <motion.section
            key="contact"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="py-20"
          >
        <div className="container mx-auto px-4">
              {/* Contact Methods */}
              <motion.div 
                className="mb-20"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold text-gray-800 mb-4">
                    Choose Your <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Preferred Way</span>
                  </h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    Multiple ways to reach us - pick what works best for you
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                  {contactMethods.map((method, index) => (
                    <motion.div
                      key={index}
                      className="group relative overflow-hidden"
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      whileHover={{ y: -8 }}
                    >
                      <div className="relative p-8 bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 h-full">
                        <div className={`w-16 h-16 ${method.bgColor} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                          <method.icon className={`w-8 h-8 ${method.textColor}`} />
                        </div>
                        
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{method.title}</h3>
                        <p className="text-gray-600 mb-4">{method.description}</p>
                        
                        <div className="space-y-2 mb-6">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Response:</span>
                            <span className="font-semibold text-gray-700">{method.responseTime}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Available:</span>
                            <span className="font-semibold text-gray-700">{method.availability}</span>
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className={`text-sm font-medium ${method.textColor} mb-2`}>Contact:</div>
                          <div className="text-gray-700 font-semibold">{method.contact}</div>
                        </div>

                        <Button className={`w-full bg-gradient-to-r ${method.color} hover:shadow-lg transition-all duration-300`}>
                          {method.action}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>

                        {/* Hover Gradient */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${method.color} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-300`}></div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Main Contact Form Section */}
              <div className="grid lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
                
                {/* Departments & Info */}
                <motion.div 
                  className="space-y-8"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
              <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Get Specific Help</h2>
                <p className="text-lg text-gray-600 mb-8">
                      Choose the right department for faster resolution of your query
                </p>
              </div>

                  <div className="space-y-4">
                    {departments.map((dept, index) => (
                      <motion.div
                        key={index}
                        className="p-6 bg-gradient-to-r from-gray-50 to-white rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300 group"
                        whileHover={{ x: 8 }}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-orange-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <dept.icon className="w-6 h-6 text-red-600" />
                          </div>
                          <div className="flex-grow">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{dept.title}</h3>
                            <p className="text-gray-600 text-sm mb-3">{dept.description}</p>
                            <a 
                              href={`mailto:${dept.email}`}
                              className="text-red-500 font-semibold text-sm hover:text-red-600 transition-colors"
                            >
                              {dept.email}
                            </a>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Enhanced Contact Form */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <Card className="shadow-2xl border-0 rounded-3xl overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-8">
                      <CardTitle className="text-3xl mb-2">Send us a Message</CardTitle>
                      <p className="text-red-100 text-lg">We'll get back to you within 24 hours</p>
                </CardHeader>
                <CardContent className="p-8">
                      <AnimatePresence>
                  {submitted && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className="mb-8 p-6 bg-green-50 border border-green-200 text-green-700 rounded-2xl text-center"
                          >
                            <CheckCircle className="w-8 h-8 mx-auto mb-3 text-green-500" />
                            <h3 className="font-bold text-lg mb-2">Message Sent Successfully!</h3>
                            <p>Thank you for reaching out. We'll get back to you soon.</p>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                    <div className="relative">
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder=" "
                        value={formData.name}
                        onChange={handleInputChange}
                              className="peer border-gray-200 focus:border-red-400 focus:ring-red-400 rounded-xl py-4 px-4 placeholder-transparent text-lg"
                        required
                      />
                            <Label htmlFor="name" className="absolute left-4 top-1/2 -translate-y-1/2 bg-white px-2 text-gray-700 font-semibold pointer-events-none transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-sm peer-focus:text-red-500">
                        Full Name *
                      </Label>
                    </div>

                    <div className="relative">
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder=" "
                        value={formData.email}
                        onChange={handleInputChange}
                              className="peer border-gray-200 focus:border-red-400 focus:ring-red-400 rounded-xl py-4 px-4 placeholder-transparent text-lg"
                        required
                      />
                            <Label htmlFor="email" className="absolute left-4 top-1/2 -translate-y-1/2 bg-white px-2 text-gray-700 font-semibold pointer-events-none transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-sm peer-focus:text-red-500">
                        Email Address *
                      </Label>
                    </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="relative">
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              placeholder=" "
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="peer border-gray-200 focus:border-red-400 focus:ring-red-400 rounded-xl py-4 px-4 placeholder-transparent text-lg"
                            />
                            <Label htmlFor="phone" className="absolute left-4 top-1/2 -translate-y-1/2 bg-white px-2 text-gray-700 font-semibold pointer-events-none transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-sm peer-focus:text-red-500">
                              Phone Number
                            </Label>
                          </div>

                          <div className="relative">
                            <select
                              name="category"
                              value={formData.category}
                              onChange={handleInputChange}
                              className="w-full border border-gray-200 focus:border-red-400 focus:ring-red-400 rounded-xl py-4 px-4 text-lg bg-white"
                              required
                            >
                              <option value="">Select Category</option>
                              <option value="general">General Support</option>
                              <option value="technical">Technical Issue</option>
                              <option value="billing">Billing & Payments</option>
                              <option value="vendor">Vendor Relations</option>
                              <option value="business">Business Inquiry</option>
                              <option value="feedback">Feedback</option>
                            </select>
                          </div>
                        </div>

                        <div className="relative">
                          <Input
                            id="subject"
                            name="subject"
                            type="text"
                            placeholder=" "
                            value={formData.subject}
                            onChange={handleInputChange}
                            className="peer border-gray-200 focus:border-red-400 focus:ring-red-400 rounded-xl py-4 px-4 placeholder-transparent text-lg"
                            required
                          />
                          <Label htmlFor="subject" className="absolute left-4 top-1/2 -translate-y-1/2 bg-white px-2 text-gray-700 font-semibold pointer-events-none transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-sm peer-focus:text-red-500">
                            Subject *
                          </Label>
                        </div>

                    <div className="relative">
                      <Textarea
                        id="message"
                        name="message"
                        placeholder=" "
                        value={formData.message}
                        onChange={handleInputChange}
                            rows={6}
                            className="peer border-gray-200 focus:border-red-400 focus:ring-red-400 rounded-xl py-4 px-4 placeholder-transparent resize-none text-lg"
                        required
                      />
                          <Label htmlFor="message" className="absolute left-4 top-6 bg-white px-2 text-gray-700 font-semibold pointer-events-none transition-all duration-200 peer-placeholder-shown:top-6 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-sm peer-focus:text-red-500">
                            Your Message *
                      </Label>
                    </div>

                    <Button 
                      type="submit" 
                          disabled={isSubmitting}
                          className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-4 text-lg transition-all duration-300 transform hover:scale-105 rounded-xl shadow-lg hover:shadow-xl"
                        >
                          {isSubmitting ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                            </>
                          )}
                    </Button>
                  </form>

                      <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                    <p className="text-sm text-gray-500">
                          By sending this message, you agree to our{' '}
                          <Link to="/privacy" className="text-red-500 hover:text-red-600">Privacy Policy</Link>
                          {' '}and{' '}
                          <Link to="/terms" className="text-red-500 hover:text-red-600">Terms of Service</Link>
                    </p>
                  </div>
                </CardContent>
              </Card>
                </motion.div>
              </div>
            </div>
          </motion.section>
        )}

        {/* FAQ Section */}
        {activeTab === 'faq' && (
          <motion.section
            key="faq"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="py-20"
          >
            <div className="container mx-auto px-4 max-w-4xl">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  Frequently Asked <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Questions</span>
                </h2>
                <p className="text-xl text-gray-600">
                  Quick answers to common questions about Dailzy
                </p>
              </div>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    className="border border-gray-200 rounded-2xl overflow-hidden"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <button
                      onClick={() => setSelectedFAQ(selectedFAQ === index ? null : index)}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div>
                        <span className="text-sm font-semibold text-red-500 mb-2 block">{faq.category}</span>
                        <h3 className="text-lg font-bold text-gray-900">{faq.question}</h3>
                      </div>
                      <motion.div
                        animate={{ rotate: selectedFAQ === index ? 45 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <HelpCircle className="w-6 h-6 text-gray-400" />
                      </motion.div>
                    </button>
                    <AnimatePresence>
                      {selectedFAQ === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="border-t border-gray-200"
                        >
                          <div className="p-6 text-gray-600 leading-relaxed">
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>

              <div className="text-center mt-12">
                <p className="text-gray-600 mb-4">Still have questions?</p>
                <Button
                  onClick={() => setActiveTab('contact')}
                  className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                >
                  Contact Support
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
          </div>
        </div>
          </motion.section>
        )}

                          {/* Locations Section */}
         {activeTab === 'locations' && (
           <motion.section
             key="locations"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -20 }}
             transition={{ duration: 0.5 }}
             className="py-20"
           >
             <div className="container mx-auto px-4">
               <div className="text-center mb-12">
                 <h2 className="text-4xl font-bold text-gray-800 mb-4">
                   Our <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Locations</span>
                 </h2>
                 <p className="text-xl text-gray-600">
                   Visit us at any of our office locations across India
                 </p>
               </div>

               <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                 {officeLocations.map((location, index) => (
                   <motion.div
                     key={index}
                     className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                     initial={{ opacity: 0, y: 30 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ delay: index * 0.2 }}
                     whileHover={{ y: -8 }}
                   >
                     <div className="text-center mb-6">
                       <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                         <Building className="w-8 h-8 text-red-600" />
                       </div>
                       <h3 className="text-2xl font-bold text-gray-900 mb-2">{location.city}</h3>
                       <span className="text-sm font-semibold text-red-500 bg-red-50 px-3 py-1 rounded-full">
                         {location.type}
                       </span>
                     </div>
                     
                     <div className="space-y-4">
                       <div className="flex items-start gap-3">
                         <MapPin className="w-5 h-5 text-gray-400 mt-1 flex-shrink-0" />
                         <span className="text-gray-600">{location.address}</span>
                       </div>
                       <div className="flex items-center gap-3">
                         <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                         <span className="text-gray-600">{location.phone}</span>
                       </div>
                     </div>

                     <Button variant="outline" className="w-full mt-6 border-red-200 text-red-600 hover:bg-red-50">
                       Get Directions
                       <ArrowRight className="w-4 h-4 ml-2" />
                     </Button>
                   </motion.div>
                 ))}
               </div>
             </div>
           </motion.section>
         )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-500 to-orange-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Experience Dailzy?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto">
              Join thousands of satisfied customers who trust Dailzy for their daily needs. Download our app and get your first order delivered in 10 minutes!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={() => {
                  // Navigate to download page
                  window.location.href = '/download';
                }}
                className="bg-white text-red-500 hover:bg-gray-100 px-8 py-4 rounded-xl text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Smartphone className="mr-3 w-5 h-5" />
                Download App
              </motion.button>
              <motion.button
                onClick={() => {
                  window.location.href = '/about';
                }}
                className="border-2 border-white text-white hover:bg-white hover:text-red-500 px-8 py-4 rounded-xl text-lg font-bold transition-all duration-300 flex items-center justify-center"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Coffee className="mr-3 w-5 h-5" />
                Learn More
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
